/**
 * useQos — QoS monitoring, video adaptation, and audio buffer management.
 *
 * - Monitors RTCPeerConnection stats for congestion detection
 * - Degrades video quality (high->mid->low->pause) before touching audio
 * - Manages AudioWorklet ring buffer for network outage resilience
 * - Reports buffer status (seconds, buffering state, catchup state)
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { useRoomContext } from '@livekit/components-react'
import { Track, RoomEvent, VideoQuality } from 'livekit-client'
import type { RemoteTrackPublication } from 'livekit-client'
import { getProcessorUrl, playBufferAt2x, SAMPLE_RATE } from './audioBufferProcessor'
import { createLogger } from '../lib/logger'

const slog = createLogger('visio-qos')

// Video quality degradation ladder (audio never touched)
const VIDEO_LADDER: VideoQuality[] = [
  VideoQuality.HIGH,
  VideoQuality.MEDIUM,
  VideoQuality.LOW,
]

const QOS_INTERVAL = 2000       // stats poll interval (ms)
const CONGESTION_THRESHOLD = 0.15 // >15% packet loss = congested
const RECOVERY_THRESHOLD = 0.03   // <3% packet loss = recovered

export interface QosStatus {
  /** Current buffer size in seconds */
  bufferSeconds: number
  /** Whether we're actively buffering (disconnected) */
  buffering: boolean
  /** Whether we're playing back buffer at 2x (catching up) */
  catchingUp: boolean
  /** Current video quality level forced by QoS (-1 = auto, 0-2 = ladder index) */
  videoLevel: number
  /** Packet loss ratio (0-1) */
  packetLoss: number
  /** Latest observed round-trip time in milliseconds */
  roundTripMs: number
}

const INITIAL: QosStatus = {
  bufferSeconds: 0,
  buffering: false,
  catchingUp: false,
  videoLevel: -1,
  packetLoss: 0,
  roundTripMs: 0,
}

export function useQos(
  enabled: boolean,
  onEvent?: (type: string, data?: Record<string, unknown>) => void,
) {
  const room = useRoomContext()
  const [status, setStatus] = useState<QosStatus>(INITIAL)

  // Refs for mutable state
  const videoLevelRef = useRef(-1) // -1 = auto
  const prevBytesRef = useRef<{ sent: number; lost: number; ts: number } | null>(null)
  const workletNodeRef = useRef<AudioWorkletNode | null>(null)
  const bufferCtxRef = useRef<AudioContext | null>(null)
  const catchingUpRef = useRef(false)
  const qosStateRef = useRef({ lossDegraded: false, rttDegraded: false })
  const onEventRef = useRef(onEvent)
  onEventRef.current = onEvent

  // ── Video degradation ──
  const degradeVideo = useCallback((): number | null => {
    const next = Math.min(videoLevelRef.current + 1, VIDEO_LADDER.length - 1)
    if (next === videoLevelRef.current) return null // already at lowest
    videoLevelRef.current = next
    const vq = VIDEO_LADDER[next]
    room?.remoteParticipants?.forEach(rp => {
      for (const [, pub] of rp.trackPublications) {
        if (pub.kind === Track.Kind.Video && pub.source === Track.Source.Camera) {
          (pub as RemoteTrackPublication).setVideoQuality(vq)
        }
      }
    })
    return next
  }, [room])

  const upgradeVideo = useCallback(() => {
    if (videoLevelRef.current <= 0) {
      videoLevelRef.current = -1 // back to auto
      return
    }
    videoLevelRef.current--
    const vq = VIDEO_LADDER[videoLevelRef.current]
    room?.remoteParticipants?.forEach(rp => {
      for (const [, pub] of rp.trackPublications) {
        if (pub.kind === Track.Kind.Video && pub.source === Track.Source.Camera) {
          (pub as RemoteTrackPublication).setVideoQuality(vq)
        }
      }
    })
  }, [room])

  // ── AudioWorklet buffer setup — captures REMOTE audio tracks ──
  useEffect(() => {
    if (!enabled || !room) return
    let cancelled = false
    const sourceNodes = new Map<string, MediaStreamAudioSourceNode>()

    // Connect a remote audio track to the worklet node
    const connectTrack = (track: any) => {
      const ctx = bufferCtxRef.current
      const node = workletNodeRef.current
      if (!ctx || !node || track.kind !== Track.Kind.Audio) return
      const mst = track.mediaStreamTrack
      if (!mst) return
      const key = track.sid || mst.id
      if (sourceNodes.has(key)) return
      const source = ctx.createMediaStreamSource(new MediaStream([mst]))
      source.connect(node)
      sourceNodes.set(key, source)
    }

    // Disconnect a remote audio track from the worklet node
    const disconnectTrack = (track: any) => {
      const key = track.sid || track.mediaStreamTrack?.id
      const source = sourceNodes.get(key)
      if (source) {
        source.disconnect()
        sourceNodes.delete(key)
      }
    }

    const setup = async () => {
      try {
        const ctx = new AudioContext({ sampleRate: SAMPLE_RATE })
        await ctx.resume()
        await ctx.audioWorklet.addModule(getProcessorUrl())
        if (cancelled) { ctx.close(); return }
        const node = new AudioWorkletNode(ctx, 'audio-buffer-processor')

        // Silent sink keeps the audio graph alive without audible output
        // (RoomAudioRenderer already plays remote audio to speakers)
        const silentGain = ctx.createGain()
        silentGain.gain.value = 0
        node.connect(silentGain)
        silentGain.connect(ctx.destination)

        workletNodeRef.current = node
        bufferCtxRef.current = ctx

        // Connect already-subscribed remote audio tracks
        room.remoteParticipants.forEach(rp => {
          for (const [, pub] of rp.trackPublications) {
            if (pub.track && pub.kind === Track.Kind.Audio && pub.isSubscribed) {
              connectTrack(pub.track)
            }
          }
        })

        // Dynamically connect/disconnect as remote tracks change
        room.on(RoomEvent.TrackSubscribed, connectTrack)
        room.on(RoomEvent.TrackUnsubscribed, disconnectTrack)

        // Listen for messages from worklet
        node.port.onmessage = (e) => {
          const { type } = e.data
          if (type === 'status') {
            setStatus(prev => ({
              ...prev,
              bufferSeconds: Math.round(e.data.bufferedSeconds * 10) / 10,
              buffering: e.data.buffering,
            }))
          } else if (type === 'buffer') {
            catchingUpRef.current = true
            setStatus(prev => ({ ...prev, catchingUp: true }))
            playBufferAt2x(ctx, e.data.samples, e.data.sampleRate, () => {
              catchingUpRef.current = false
              setStatus(prev => ({ ...prev, catchingUp: false, bufferSeconds: 0 }))
            })
          }
        }
      } catch (err) {
        console.warn('[useQos] AudioWorklet setup failed:', err)
      }
    }
    setup()

    return () => {
      cancelled = true
      room.off(RoomEvent.TrackSubscribed, connectTrack)
      room.off(RoomEvent.TrackUnsubscribed, disconnectTrack)
      sourceNodes.forEach(s => s.disconnect())
      sourceNodes.clear()
      workletNodeRef.current?.disconnect()
      workletNodeRef.current = null
      bufferCtxRef.current?.close().catch(() => {})
      bufferCtxRef.current = null
    }
  }, [enabled, room]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Network state: start/stop buffering ──
  useEffect(() => {
    if (!enabled || !room) return
    const onReconnecting = () => {
      workletNodeRef.current?.port.postMessage({ command: 'startBuffering' })
      setStatus(prev => ({ ...prev, buffering: true }))
    }
    const onReconnected = () => {
      workletNodeRef.current?.port.postMessage({ command: 'stopBuffering' })
      // Flush buffer and play at 2x
      workletNodeRef.current?.port.postMessage({ command: 'flush' })
      setStatus(prev => ({ ...prev, buffering: false }))
    }
    const onDisconnected = () => {
      workletNodeRef.current?.port.postMessage({ command: 'stopBuffering' })
      setStatus(prev => ({ ...prev, buffering: false }))
    }
    room.on(RoomEvent.Reconnecting, onReconnecting)
    room.on(RoomEvent.Reconnected, onReconnected)
    room.on(RoomEvent.Disconnected, onDisconnected)
    return () => {
      room.off(RoomEvent.Reconnecting, onReconnecting)
      room.off(RoomEvent.Reconnected, onReconnected)
      room.off(RoomEvent.Disconnected, onDisconnected)
    }
  }, [enabled, room])

  // ── Stats polling: congestion detection + buffer status ──
  useEffect(() => {
    if (!enabled) return
    const id = setInterval(async () => {
      // Poll buffer status from worklet
      workletNodeRef.current?.port.postMessage({ command: 'getStatus' })

      // Poll RTCPeerConnection stats for congestion
      try {
        const pc = (room as any)?.engine?.pcManager?.publisher?.getStats?.()
          || (room as any)?.engine?.publisher?.pc?.getStats?.()
        if (!pc) return
        const stats: RTCStatsReport = await pc
        let totalSent = 0
        let totalLost = 0
        let maxRttSeconds = 0
        let rttObserved = false
        stats.forEach((s: any) => {
          if (s.type === 'outbound-rtp' && s.kind === 'audio') {
            totalSent += s.packetsSent || 0
          }
          if (s.type === 'remote-inbound-rtp' && s.kind === 'audio') {
            totalLost += s.packetsLost || 0
            const currentRtt = Number(s.roundTripTime || 0)
            const averageRtt = s.roundTripTimeMeasurements > 0
              ? Number(s.totalRoundTripTime || 0) / Number(s.roundTripTimeMeasurements)
              : 0
            rttObserved = rttObserved
              || s.roundTripTime !== undefined
              || s.roundTripTimeMeasurements > 0
            maxRttSeconds = Math.max(maxRttSeconds, currentRtt, averageRtt)
          }
        })

        const prev = prevBytesRef.current
        const now = Date.now()
        if (prev && now > prev.ts) {
          const sentDelta = totalSent - prev.sent
          const lostDelta = Math.max(0, totalLost - prev.lost)
          const lossRatio = sentDelta > 0 ? lostDelta / sentDelta : 0
          const roundTripMs = Math.min(10_000, Math.max(0, Math.round(maxRttSeconds * 1000)))

          setStatus(p => ({
            ...p,
            packetLoss: Math.round(lossRatio * 100) / 100,
            roundTripMs,
            videoLevel: videoLevelRef.current,
          }))

          // Congestion transitions only: do not emit on every 2 s stats tick.
          if (lossRatio > CONGESTION_THRESHOLD && !qosStateRef.current.lossDegraded) {
            qosStateRef.current.lossDegraded = true
            const level = degradeVideo()
            if (level !== null) {
              const measures = {
                reason: 'packet_loss',
                packetLossPct: Math.min(100, Math.round(lossRatio * 100)),
                thresholdPct: CONGESTION_THRESHOLD * 100,
                videoLevel: level,
              }
              slog.warn('qos-degrade', measures)
              onEventRef.current?.('qos_degraded', measures)
            }
          }
          if (lossRatio < RECOVERY_THRESHOLD) {
            if (qosStateRef.current.lossDegraded) {
              qosStateRef.current.lossDegraded = false
              const measures = {
                reason: 'packet_loss',
                packetLossPct: Math.max(0, Math.round(lossRatio * 100)),
                recoveryThresholdPct: RECOVERY_THRESHOLD * 100,
              }
              slog.info('qos-restore', measures)
              onEventRef.current?.('qos_recovered', measures)
            }
            if (videoLevelRef.current >= 0) upgradeVideo()
          }

          if (rttObserved && roundTripMs > 400 && !qosStateRef.current.rttDegraded) {
            qosStateRef.current.rttDegraded = true
            const measures = { reason: 'rtt', roundTripMs, thresholdMs: 400 }
            slog.warn('qos-rtt-high', measures)
            onEventRef.current?.('qos_degraded', measures)
          } else if (rttObserved && roundTripMs < 250 && qosStateRef.current.rttDegraded) {
            qosStateRef.current.rttDegraded = false
            const measures = { reason: 'rtt', roundTripMs, recoveryThresholdMs: 250 }
            slog.info('qos-rtt-recovered', measures)
            onEventRef.current?.('qos_recovered', measures)
          }
        }
        prevBytesRef.current = { sent: totalSent, lost: totalLost, ts: now }
      } catch { /* stats unavailable */ }
    }, QOS_INTERVAL)

    return () => clearInterval(id)
  }, [enabled, room, degradeVideo, upgradeVideo])

  return status
}
